// Sensor de skew a nivel de RED para las Server Actions.
//
// Incidente George Burger (2026-09-01, 02:01Z): tres deploys en una noche y
// una tablet ACTIVA con el bundle viejo → cada POST de action respondía 404
// "Failed to find Server Action" y el operador veía un error crudo. El
// detector de versionSkew.ts sí reconoce ese error, pero solo si llega a un
// handler global — y los callers del POS lo atrapan en su try/catch y lo
// muestran como toast (el hueco SESS-15). Cablear reportCaughtVersionSkew()
// caller por caller no escala: cada catch nuevo re-abre el hueco.
//
// Aquí se observa el TRANSPORTE: Next serializa toda Server Action como POST
// con el header `Next-Action`; si esa respuesta es 404, el deployment vivo ya
// no reconoce la action del bundle cargado — skew, sin otra causa posible
// (la ruta de la página existe; lo que falta es la action). Se despacha
// VERSION_SKEW_EVENT y BuildWatcher hace el resto (aviso + recarga en idle,
// respetando holdReload de cobros/impresiones). El sensor observa y avisa:
// JAMÁS altera la respuesta ni lanza.

import { VERSION_SKEW_EVENT } from "./versionSkew"

/** POST de una Server Action: el transporte de Next siempre manda `Next-Action`. */
export function isServerActionPost(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = init?.method ?? (input instanceof Request ? input.method : "GET")
  if (method.toUpperCase() !== "POST") return false
  if (hasNextActionHeader(init?.headers)) return true
  return input instanceof Request && input.headers.has("next-action")
}

function hasNextActionHeader(headers: HeadersInit | undefined): boolean {
  if (!headers) return false
  if (headers instanceof Headers) return headers.has("next-action")
  if (Array.isArray(headers)) return headers.some(([name]) => name.toLowerCase() === "next-action")
  return Object.keys(headers).some((name) => name.toLowerCase() === "next-action")
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

/**
 * Envuelve un fetch para avisar (`onSkew`) cuando un POST de Server Action
 * responde 404. La respuesta y los rechazos pasan INTACTOS; un `onSkew` que
 * lance tampoco puede romper el fetch. Separado del glue de window para poder
 * probarse en jest-node sin DOM.
 */
export function wrapFetchForActionSkew(original: FetchLike, onSkew: () => void): FetchLike {
  return async (input, init) => {
    const res = await original(input, init)
    try {
      if (res.status === 404 && isServerActionPost(input, init)) onSkew()
    } catch {
      // El sensor nunca puede costarle nada a una respuesta que ya llegó.
    }
    return res
  }
}

type SensorWindow = Window & typeof globalThis & { __redyActionSkewUninstall?: () => void }

/**
 * Instala el sensor sobre window.fetch. Devuelve el uninstall (restaura el
 * fetch previo). Idempotente: una segunda instalación (StrictMode monta los
 * efectos dos veces) devuelve el uninstall vigente sin envolver de nuevo.
 */
export function installActionSkewSensor(): () => void {
  if (typeof window === "undefined") return () => {}
  const w = window as SensorWindow
  if (w.__redyActionSkewUninstall) return w.__redyActionSkewUninstall

  const original = w.fetch.bind(w)
  const wrapped = wrapFetchForActionSkew(original, () => {
    w.dispatchEvent(new Event(VERSION_SKEW_EVENT))
  })
  w.fetch = wrapped as typeof w.fetch

  const uninstall = () => {
    if (w.fetch === wrapped) w.fetch = original as typeof w.fetch
    delete w.__redyActionSkewUninstall
  }
  w.__redyActionSkewUninstall = uninstall
  return uninstall
}
